import * as React from 'react'
import { X } from 'lucide-react'
import { toast } from 'sonner'

import { cn } from '@/lib/utils'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function isValidEmailAddress(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim())
}

interface RecipientChipsInputProps {
  id?: string
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  placeholder?: string
  'aria-label'?: string
}

/**
 * RecipientChipsInput - editable To/CC/BCC style email chip field.
 *
 * Commits the current typed text into a chip on Enter, comma, semicolon,
 * blur, or Tab. Backspace on an empty field removes the most recently
 * added chip. Pasting a comma/semicolon/whitespace-separated list splits
 * it into multiple chips.
 */
export function RecipientChipsInput({
  id,
  value,
  onChange,
  disabled = false,
  placeholder = 'Add an email and press Enter',
  ...rest
}: RecipientChipsInputProps) {
  const [draft, setDraft] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)

  const commit = React.useCallback((raw: string, options: { notifyOnInvalid?: boolean } = {}) => {
    const candidate = raw.trim().replace(/[,;]+$/, '')
    if (!candidate) return

    if (!isValidEmailAddress(candidate)) {
      if (options.notifyOnInvalid) {
        toast.error(`"${candidate}" isn't a valid email address`)
      }
      return
    }

    setDraft('')
    const alreadyPresent = value.some((email) => email.toLowerCase() === candidate.toLowerCase())
    if (!alreadyPresent) {
      onChange([...value, candidate])
    }
  }, [onChange, value])

  // Handles a paste of several addresses at once. Unlike `commit`, this
  // resolves every candidate against the *same* snapshot of `value` and
  // issues a single `onChange` call — calling `commit` once per address in a
  // loop instead would have every call close over the same stale `value` and
  // only the last one would stick. Also reports every rejected address in one
  // toast, since a malformed entry in a paste has no other way to surface.
  const commitMany = React.useCallback((rawParts: string[]) => {
    const seen = new Set(value.map((email) => email.toLowerCase()))
    const added: string[] = []
    const invalid: string[] = []

    for (const raw of rawParts) {
      const candidate = raw.trim().replace(/[,;]+$/, '')
      if (!candidate) continue

      if (!isValidEmailAddress(candidate)) {
        invalid.push(candidate)
        continue
      }

      const key = candidate.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      added.push(candidate)
    }

    setDraft('')
    if (added.length > 0) {
      onChange([...value, ...added])
    }
    if (invalid.length > 0) {
      toast.error(
        invalid.length === 1
          ? `"${invalid[0]}" isn't a valid email address`
          : `${invalid.length} entries weren't valid email addresses: ${invalid.join(', ')}`
      )
    }
  }, [onChange, value])

  const removeAt = React.useCallback((index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }, [onChange, value])

  return (
    <div
      className={cn(
        'flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-2 py-1.5 shadow-xs transition-[color,box-shadow]',
        'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
        disabled && 'cursor-not-allowed opacity-50'
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((email, index) => (
        <span
          key={`${email}-${index}`}
          className="flex items-center gap-1 rounded-md border bg-muted/50 px-2 py-0.5 text-xs"
        >
          {email}
          {!disabled && (
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation()
                removeAt(index)
              }}
              aria-label={`Remove ${email}`}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={draft}
        disabled={disabled}
        placeholder={value.length === 0 ? placeholder : ''}
        className="min-w-[8rem] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            // Always suppress — an uncommitted Enter must never bubble up
            // and submit the surrounding compose form.
            e.preventDefault()
            if (draft.trim()) commit(draft, { notifyOnInvalid: true })
            return
          }
          if (e.key === ',' || e.key === ';') {
            if (draft.trim()) {
              e.preventDefault()
              commit(draft, { notifyOnInvalid: true })
            }
            return
          }
          if (e.key === 'Tab') {
            if (draft.trim()) commit(draft)
            return
          }
          if (e.key === 'Backspace' && !draft && value.length > 0) {
            removeAt(value.length - 1)
          }
        }}
        onBlur={() => {
          if (draft.trim()) commit(draft)
        }}
        onPaste={(e) => {
          const text = e.clipboardData.getData('text')
          if (!/[,;\s]/.test(text)) return
          e.preventDefault()
          commitMany(text.split(/[,;\s]+/).filter(Boolean))
        }}
        {...rest}
      />
    </div>
  )
}
